import React, { useState, useRef, useEffect } from 'react';
import { Book, Video, Lightbulb, Award, Settings, ChevronRight } from 'lucide-react';
import styles from './Documentation.module.css';

const Documentation = () => {
    const [activeCategory, setActiveCategory] = useState('getting-started');
    const [activeSection, setActiveSection] = useState('introduction');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showSidebar, setShowSidebar] = useState(true);
    const sidebarRef = useRef(null);

    const categories = {
        'getting-started': {
            icon: <Book size={18} />,
            title: 'Getting Started',
            sections: [
                {
                    id: 'introduction',
                    title: 'Introduction to TopSpeaks',
                    content: {
                        overview: 'TopSpeaks is an advanced AI-powered platform designed to enhance your presentation skills through real-time analysis and feedback.',
                        features: [
                            'Real-time speech analysis and feedback',
                            'AI-powered presentation generation',
                            'Expert coaching sessions',
                            'Progress tracking and analytics'
                        ],
                        requirements: 'A modern web browser and microphone are required for recording features.'
                    }
                },
                {
                    id: 'quick-setup',
                    title: 'Quick Setup Guide',
                    content: {
                        steps: [
                            'Create your account or sign in',
                            'Grant microphone permissions for recording',
                            'Complete your profile for personalized feedback',
                            'Start your first recording or generate a presentation'
                        ],
                        tips: 'We recommend using a high-quality microphone for better speech recognition accuracy.'
                    }
                },
                {
                    id: 'first-recording',
                    title: 'Your First Recording',
                    content: {
                        preparation: "Ensure you're in a quiet environment with minimal background noise.",
                        steps: [
                            'Click "Start Recording" on the recording page',
                            'Begin your presentation when ready',
                            'View real-time feedback and transcription',
                            'Stop recording when finished',
                            'Review AI-generated feedback and insights'
                        ]
                    }
                }
            ]
        },
        'recording': {
            icon: <Video size={18} />,
            title: 'Recording Features',
            sections: [
                {
                    id: 'recording-setup',
                    title: 'Recording Setup',
                    content: {
                        requirements: [
                            'Microphone access enabled',
                            'Stable internet connection',
                            'Chrome, Firefox, Edge or modern browser'
                        ],
                        optimization: 'Position your microphone 6-12 inches from your mouth for optimal audio quality.',
                        timeLimit: 'Free tier: 1 minute, Pro tier: 10 minutes, Student tier: 5 minutes'
                    }
                },
                {
                    id: 'audio-requirements',
                    title: 'Audio Requirements',
                    content: {
                        specifications: {
                            sampleRate: '44.1kHz standard audio sample rate',
                            channels: 'Stereo audio supported',
                            format: 'WebM audio format with Opus codec'
                        },
                        recommendations: [
                            'Use a dedicated microphone for better quality',
                            'Test audio levels before recording',
                            'Minimize background noise'
                        ]
                    }
                },
                {
                    id: 'feedback-analysis',
                    title: 'Understanding AI Feedback',
                    content: {
                        metrics: [
                            'Speaking rate (words per minute)',
                            'Filler word usage percentage',
                            'Recommendations'
                        ],
                        interpretation: 'The AI provides color-coded feedback: yellow for fillers needing attention, red for immediate improvement needed.'
                    }
                }
            ]
        },
        'ai-features': {
            icon: <Lightbulb size={18} />,
            title: 'AI Features',
            sections: [
                {
                    id: 'slide-generation',
                    title: 'AI Slide Generation',
                    content: {
                        capabilities: [
                            'Convert text to structured presentations',
                            'Smart layout optimization',
                            'Content summarization',
                            'Visual element suggestions'
                        ],
                        usage: 'Input your topic or paste your content, and the AI will generate professional slides within seconds.'
                    }
                },
                {
                    id: 'coaching-ai',
                    title: 'AI Coaching System',
                    content: {
                        features: [
                            'Get personalized recommendations',
                            'Choose what to share with AI coach',
                            'Get nstant mprovement suggestions',
                            'Get practice scenarios suggestions'
                        ],
                        adaptive_Learning: 'The AI adapts to your progress and adjusts recommendations accordingly.'
                    }
                },
                {
                    id: 'analytics',
                    title: 'Analytics & Insights',
                    content: {
                        metrics: [
                            'Historical performance trends',
                            'Speaking rate analysis',
                            'Improvement tracking',
                            'Comparative analysis'
                        ],
                        visualization: 'Interactive charts and graphs display your progress over time.'
                    }
                }
            ]
        },
        'best-practices': {
            icon: <Award size={18} />,
            title: 'Best Practices',
            sections: [
                {
                    id: 'presentation-tips',
                    title: 'Presentation Tips',
                    content: {
                        delivery: [
                            'Maintain consistent pace',
                            'Use appropriate pauses',
                            'Engage with natural gestures',
                            'Project confidence'
                        ],
                        structure: 'Follow the 3-part structure: Introduction, Main Points, Conclusion'
                    }
                },
                {
                    id: 'speech-techniques',
                    title: 'Speech Techniques',
                    content: {
                        methods: [
                            'Vocal variety',
                            'Strategic pausing',
                            'Emphasis techniques',
                            'Audience engagement'
                        ],
                        exercises: 'Practice vocal exercises before presentations for better clarity.'
                    }
                },
                {
                    id: 'improvement-strategies',
                    title: 'Improvement Strategies',
                    content: {
                        approaches: [
                            'Regular practice sessions',
                            'Recording review',
                            'Peer feedback',
                            'Expert coaching'
                        ],
                        planning: 'Set specific improvement goals and track progress using analytics.'
                    }
                }
            ]
        },
        'settings': {
            icon: <Settings size={18} />,
            title: 'Settings & Configuration',
            sections: [
                {
                    id: 'account-settings',
                    title: 'Account Settings',
                    content: {
                        options: [
                            'Profile management',
                            'Subscription settings',
                            'Language preferences',
                            'Notification settings'
                        ],
                        security: 'Do not share your email or password with anyone for enhanced account security.'
                    }
                },
                {
                    id: 'notifications',
                    title: 'Notification Preferences',
                    content: {
                        types: [
                            'Session reminders',
                            'Progress updates',
                            'Coaching availability',
                            'System updates'
                        ],
                        channels: 'Only in-app notifications.'
                    }
                },
                {
                    id: 'privacy',
                    title: 'Privacy & Security',
                    content: {
                        settings: [
                            'Recording privacy options',
                            'Data retention settings',
                            'Sharing preferences',
                            'Account security'
                        ],
                        dataHandling: 'All recordings are encrypted and stored securely.'
                    }
                }
            ]
        }
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            setShowSidebar(window.innerWidth >= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getContent = (categoryId, sectionId) => {
        const category = categories[categoryId];
        const section = category.sections.find(s => s.id === sectionId);

        if (!section) return null;

        return (
            <div className={styles.contentSection}>
                <div className={styles.contentHeader}>
                    <h1>{section.title}</h1>
                </div>
                <div className={styles.contentBody}>
                    {Object.entries(section.content).map(([key, value]) => (
                        <div key={key} className={styles.contentBlock}>
                            <h2 className={styles.contentBlockTitle}>
                                {key.charAt(0).toUpperCase() + key.slice(1)}
                            </h2>
                            {Array.isArray(value) ? (
                                <ul className={styles.contentList}>
                                    {value.map((item, index) => (
                                        <li key={index}>{item}</li>
                                    ))}
                                </ul>
                            ) : typeof value === 'object' ? (
                                <div className={styles.contentObject}>
                                    {Object.entries(value).map(([subKey, subValue]) => (
                                        <div key={subKey} className={styles.contentSubBlock}>
                                            <h3>{subKey}</h3>
                                            <p>{subValue}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p>{value}</p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const handleSectionClick = (sectionId) => {
        setActiveSection(sectionId);
        if (isMobile) setShowSidebar(false);
    };

    return (
        <div className={styles.docsContainer}>
            {isMobile && (
                <button
                    className={styles.sidebarToggle}
                    onClick={() => setShowSidebar(!showSidebar)}
                    aria-label="Toggle sidebar"
                >
                    <ChevronRight size={20} />
                </button>
            )}

            <aside className={`${styles.sidebar} ${!showSidebar ? styles.sidebarHidden : ''}`} ref={sidebarRef}>
                <nav className={styles.categoryNav}>
                    {Object.entries(categories).map(([key, category]) => (
                        <div key={key} className={styles.categoryGroup}>
                            <button
                                className={`${styles.categoryButton} ${activeCategory === key ? styles.active : ''}`}
                                onClick={() => setActiveCategory(key)}
                            >
                                <span className={styles.categoryIcon}>{category.icon}</span>
                                {category.title}
                            </button>

                            <div className={`${styles.sectionList} ${activeCategory === key ? styles.expanded : ''}`}>
                                {category.sections.map((section) => (
                                    <button
                                        key={section.id}
                                        className={`${styles.sectionButton} ${activeSection === section.id ? styles.activeSection : ''}`}
                                        onClick={() => handleSectionClick(section.id)}
                                    >
                                        {section.title}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>
            </aside>

            <main className={styles.mainContent}>
                <div className={styles.breadcrumbs}>
                    {categories[activeCategory].title} /
                    {categories[activeCategory].sections.find(s => s.id === activeSection)?.title}
                </div>

                {getContent(activeCategory, activeSection)}
            </main>
        </div>
    );
};

export default Documentation;